/**
 * @file useTemplateApi.js
 * @description API composable for template operations
 * Handles all HTTP requests related to equipment, device, and compound templates
 */

import { ref, toValue } from 'vue';
import { useQuasar } from 'quasar';
import brickSchemaService from '@/services/brickSchemaService';

/**
 * Composable for template API operations
 * @param {number|Ref<number>} siteId (can be a ref or computed) - The site ID (optional, not all template endpoints need it)
 * @returns {Object} Template API methods and state
 */
export function useTemplateApi(siteId = null) {
  const $q = useQuasar();

  // State
  const loadingTemplates = ref(false);
  const saving = ref(false);
  const availableTemplates = ref([]);

  /**
   * Load all templates (equipment, device, and compound)
   * @returns {Promise<Array>} Combined templates array
   */
  async function loadTemplates() {
    loadingTemplates.value = true;
    try {
      // Load all template types
      const [equipmentResult, deviceResult, compoundResult] = await Promise.all([
        brickSchemaService.templates.getEquipmentTemplates(),
        brickSchemaService.templates.getDeviceTemplates(),
        brickSchemaService.templates.getCompoundTemplates(),
      ]);

      // Handle equipment templates
      const equipmentArray = equipmentResult.success
        ? (equipmentResult.data?.data || equipmentResult.data || [])
        : [];

      const equipmentTemplates = (Array.isArray(equipmentArray) ? equipmentArray : []).map((template) => ({
        ...template,
        template_type: 'equipment',
      }));

      // Handle device templates
      const deviceTemplates = (deviceResult.success ? (deviceResult.data || []) : []).map((template) => ({
        ...template,
        template_type: 'device',
        brick_class: template.brick_class || template.device_type || '',
        usage_count: template.usage_count || 0,
      }));

      // Handle compound templates
      const compoundTemplatesList = (compoundResult.success ? (compoundResult.data || []) : []).map((template) => ({
        ...template,
        id: template.id,
        name: template.name || 'Unnamed Compound',
        description: template.description || '',
        brick_class: template.brick_class || 'Compound Assembly',
        template_type: 'compound',
        is_active: template.is_active !== false,
        usage_count: template.usage_count || 0,
        created_at: template.created_at,
        component_count: template.components?.length || 0,
        total_points: template.components?.reduce((sum, comp) => sum + (comp.points?.length || 0), 0) || 0,
      }));

      availableTemplates.value = [...equipmentTemplates, ...deviceTemplates, ...compoundTemplatesList];
      return availableTemplates.value;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load templates: ${error.message}`,
      });
      return [];
    } finally {
      loadingTemplates.value = false;
    }
  }

  /**
   * Load equipment templates only
   * @returns {Promise<Array>} Equipment templates
   */
  async function loadEquipmentTemplates() {
    try {
      const result = await brickSchemaService.templates.getEquipmentTemplates();

      if (!result.success) {
        throw new Error(result.error);
      }

      const equipmentArray = result.data?.data || result.data || [];
      return Array.isArray(equipmentArray) ? equipmentArray : [];
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load equipment templates: ${error.message}`,
      });
      return [];
    }
  }

  /**
   * Load compound templates
   * @returns {Promise<Array>} Compound templates
   */
  async function loadCompoundTemplates() {
    try {
      const result = await brickSchemaService.templates.getCompoundTemplates();

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data || [];
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load compound templates: ${error.message}`,
      });
      return [];
    }
  }

  /**
   * Delete template
   * @param {Object} template - Template to delete
   * @returns {Promise<void>}
   */
  async function deleteTemplate(template) {
    try {
      const result = await brickSchemaService.templates.delete(template.template_type, template.id);

      if (!result.success) {
        // Check if error data includes usage information
        if (result.data?.usage) {
          throw Object.assign(new Error(result.error), { usage: result.data.usage });
        }
        throw new Error(result.error);
      }

      $q.notify({
        type: 'positive',
        message: `Template "${template.name}" deleted successfully`,
      });

      // Remove from local list
      availableTemplates.value = availableTemplates.value.filter((t) => t.id !== template.id);
    } catch (error) {

      // If error has usage data, include it in the notification
      if (error.usage) {
        $q.notify({
          type: 'negative',
          message: `Cannot delete template: ${error.message}`,
          html: true,
        });
      } else {
        $q.notify({
          type: 'negative',
          message: `Failed to delete template: ${error.message}`,
        });
      }
      throw error;
    }
  }

  /**
   * Save entity as template
   * @param {Object} templateData - Template data to save
   * @param {string} templateType - Type of template ('equipment', 'device', 'compound')
   * @returns {Promise<Object>} Created template
   */
  async function saveAsTemplate(templateData, templateType) {
    try {
      saving.value = true;

      const result = await brickSchemaService.templates.createEquipmentTemplate(templateData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newTemplate = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Template "${templateData.name}" saved successfully`,
      });

      // Add to local list
      availableTemplates.value.push({
        ...newTemplate,
        template_type: templateType,
      });

      return newTemplate;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to save template: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Sync entity with template
   * @param {string} entityType - Type of entity ('equipment', 'device')
   * @param {number} entityId - Entity ID
   * @param {number} templateId - Template ID
   * @returns {Promise<Object>} Updated entity
   */
  async function syncWithTemplate(entityType, entityId, templateId) {
    if (!siteId) {
      throw new Error('Site ID is required for syncing with template');
    }

    try {
      saving.value = true;

      // Use equipment sync endpoint
      const result = await brickSchemaService.equipment.syncWithTemplate(toValue(siteId), entityId);

      if (!result.success) {
        throw new Error(result.error);
      }

      const updatedEntity = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: 'Entity synced with template successfully',
      });

      return updatedEntity;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to sync with template: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Detach entity from template
   * @param {string} entityType - Type of entity ('equipment', 'device')
   * @param {number} entityId - Entity ID
   * @returns {Promise<Object>} Updated entity
   */
  async function detachFromTemplate(entityType, entityId) {
    // This functionality is not in the service yet, keeping the composable method
    // for backward compatibility
    throw new Error('Detach from template not yet implemented in service');
  }

  /**
   * Get template usage locations
   * @param {number} templateId - Template ID
   * @param {string} templateType - Type of template
   * @returns {Promise<Array>} Usage locations
   */
  async function getTemplateUsage(templateId, templateType) {
    try {
      const result = await brickSchemaService.templates.getUsage(templateType, templateId);

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data || [];
    } catch (error) {
      return [];
    }
  }

  return {
    // State
    loadingTemplates,
    saving,
    availableTemplates,

    // Methods
    loadTemplates,
    loadEquipmentTemplates,
    loadCompoundTemplates,
    deleteTemplate,
    saveAsTemplate,
    syncWithTemplate,
    detachFromTemplate,
    getTemplateUsage,
  };
}
